import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Copy, TrendingUp, Award, Wallet, Trophy, Crown,
  ArrowUpRight, BadgeDollarSign, ShoppingBag, Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import TopSellerPopup from '../components/TopSellerPopup';
import GrowthChart from '../components/GrowthChart';
import DashboardTutorial from '../components/DashboardTutorial';
import PinnedAnnouncementCard from '../components/ambassador/PinnedAnnouncementCard';
import NotificationsBell from '../components/ambassador/NotificationsBell';
import WithdrawModal from '../components/ambassador/WithdrawModal';
import MotivationStrip from '../components/ambassador/MotivationStrip';
import MyProductAnalytics from '../components/ambassador/MyProductAnalytics';
import PrizeClaimReminder from '../components/ambassador/PrizeClaimReminder';

const API = `${window.location.origin}/api`;

const TIER_BADGE_COLORS = {
  Bronze: { bg: '#7B3F00', text: '#FFD9A0' },
  Silver: { bg: '#777', text: '#FFF' },
  Gold: { bg: '#7B5E15', text: '#FFE69E' },
};

export default function AmbassadorDashboardPage() {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [sales, setSales] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [scope, setScope] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topSellerData, setTopSellerData] = useState(null);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

  useEffect(() => {
    // Wait for AuthContext to finish its initial /auth/me check before
    // deciding whether the user is logged in — otherwise we briefly show the
    // "Please log in" gate on every page refresh while the token validates.
    if (authLoading) return;
    if (!isAuthenticated) {
      setError('Please log in to view your dashboard.');
      setLoading(false);
      return;
    }
    // Step 1: gate by tester-kit status — if not active yet, route to /pending-payment or checkout
    axios.get(`${API}/ambassador/me`, { headers: headers() })
      .then(profileRes => {
        const a = profileRes.data?.ambassador;
        if (!a) {
          setError('No ambassador profile found.');
          setLoading(false);
          return;
        }
        if (a.status === 'pending_tester_kit') {
          // Decide: have they submitted a paid_pending_approval order yet?
          axios.get(`${API}/tester-kit/my-order`, { headers: headers() })
            .then(o => {
              const ord = o.data?.order;
              if (ord && ord.status === 'paid_pending_approval') navigate('/pending-payment');
              else navigate('/tester-kit-checkout');
            })
            .catch(() => navigate('/tester-kit-checkout'));
          return;
        }
        setProfile(profileRes.data);
        // Step 2: load all dashboard data
        Promise.all([
          axios.get(`${API}/ambassador/me/referrals`, { headers: headers() }).catch(() => ({ data: [] })),
          axios.get(`${API}/ambassador/me/sales`, { headers: headers() }).catch(() => ({ data: null })),
          axios.get(`${API}/ambassador/me/wallet`, { headers: headers() }).catch(() => ({ data: null })),
          axios.get(`${API}/leaderboard?scope=month&limit=10`).catch(() => ({ data: { rows: [] } })),
          axios.get(`${API}/leaderboard/top-seller`).catch(() => ({ data: null })),
        ]).then(([r, s, w, lb, ts]) => {
          setReferrals(r.data || []);
          setSales(s.data);
          setWallet(w.data);
          setLeaderboard(lb.data?.rows || []);
          setTopSellerData(ts.data);
        }).finally(() => setLoading(false));
      })
      .catch(e => {
        setError(e.response?.data?.detail || 'Unable to load dashboard');
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading]);

  const reloadLeaderboard = (newScope) => {
    setScope(newScope);
    axios.get(`${API}/leaderboard?scope=${newScope}&limit=10`)
      .then(r => setLeaderboard(r.data?.rows || []))
      .catch(() => {});
  };

  const copyLink = () => {
    if (!profile) return;
    navigator.clipboard.writeText(profile.referral_url);
    toast.success('Referral link copied — share it!');
  };

  const copyAmbReferralLink = () => {
    if (!profile?.ambassador?.referral_slug) return;
    const link = `${window.location.origin}/ambassador-apply?ref=${profile.ambassador.referral_slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Ambassador-recruit link copied — earn ₱200 per signup!');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/40 text-sm font-sans">Loading…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md text-center">
        <p className="text-white/60 text-sm font-sans mb-2">{error}</p>
        <Link to="/login" className="text-[#D4AF37] hover:text-white text-xs uppercase tracking-[0.2em] underline">Log in</Link>
      </div>
    </div>
  );
  if (!profile) return null;

  const a = profile.ambassador;
  const myMonthBottles = sales?.this_month?.bottles || 0;
  const myMonthRevenue = sales?.this_month?.revenue || 0;
  const myAllTimeBottles = sales?.all_time?.bottles || 0;
  const tierBadge = sales?.tier_badge;
  const tierColor = tierBadge ? TIER_BADGE_COLORS[tierBadge] : null;
  const isTopSeller = topSellerData?.top?.ambassador_id === a.id;
  const now = new Date();

  return (
    <div data-testid="ambassador-dashboard" className="min-h-screen px-4 sm:px-6 lg:px-8 py-10 max-w-6xl mx-auto">
      {/* First-visit tutorial — recurs until completed */}
      <DashboardTutorial
        alreadyCompleted={!!a.tutorial_completed}
        onComplete={() => setProfile(p => p ? { ...p, ambassador: { ...p.ambassador, tutorial_completed: true } } : p)}
      />

      {/* Top Seller popup */}
      {isTopSeller && topSellerData?.top && (
        <TopSellerPopup data={topSellerData} ambassadorSlug={a.referral_slug} />
      )}

      {/* Withdraw modal */}
      {showWithdraw && (
        <WithdrawModal
          profile={a}
          onClose={() => setShowWithdraw(false)}
          onSuccess={() => {
            // Re-fetch wallet to reflect the locked balance immediately
            axios.get(`${API}/ambassador/me/wallet`, { headers: headers() })
              .then(w => setWallet(w.data))
              .catch(() => {});
          }}
        />
      )}

      {/* Pinned announcement (golden glow) — shows at top when unread */}
      <PinnedAnnouncementCard />

      {/* Header */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2 font-sans">Ambassador Dashboard</p>
          <h1 className="text-4xl tracking-tight leading-none font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {user?.name?.split(' ')[0] || 'Ambassador'}, {isTopSeller ? <span className="text-[#D4AF37] italic">you&apos;re the Top Seller!</span> : 'let\'s push.'}
          </h1>
          <p className="text-white/40 text-sm mt-2 font-sans flex items-center gap-2 flex-wrap">
            {tierBadge && tierColor && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] font-medium" style={{ background: tierColor.bg, color: tierColor.text }}>
                <Award className="w-3 h-3" /> {tierBadge}
              </span>
            )}
            <span className="text-white/40">{a.tier?.name || 'Starter'} tier · {referrals.length} referrals</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <NotificationsBell />
          <Link
            to={`/certificate/${now.getFullYear()}/${now.getMonth() + 1}`}
            data-tutorial-target="tutorial-cert"
            className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] border border-[#D4AF37]/40 px-4 py-2 hover:bg-[#D4AF37]/10 transition-colors flex items-center gap-2"
            data-testid="dashboard-cert-link"
          >
            <Trophy className="w-3.5 h-3.5" /> Certificate
          </Link>
        </div>
      </div>

      {/* Stat grid */}
      <div data-tutorial-target="tutorial-stats" className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <StatCard icon={ShoppingBag} label="Bottles this month" value={myMonthBottles} accent="#D4AF37" testid="stat-month-bottles" />
        <StatCard icon={TrendingUp} label="Revenue this month" value={`₱${Number(myMonthRevenue).toLocaleString()}`} accent="#1C7D3F" testid="stat-month-revenue" />
        <StatCard icon={Crown} label="All-time bottles" value={myAllTimeBottles} accent="#D4AF37" testid="stat-alltime-bottles" />
        <button
          type="button"
          onClick={() => setShowWithdraw(true)}
          className="text-left bg-[#141414] border border-[#D4AF37]/30 p-4 sm:p-5 hover:border-[#D4AF37] hover:bg-[#D4AF37]/[0.03] transition-colors relative group"
          data-testid="stat-wallet"
        >
          <Wallet className="w-4 h-4 mb-2" style={{ color: '#D4AF37' }} />
          <p className="text-2xl text-white font-medium font-sans leading-tight truncate" title={`₱${Number(wallet?.balance ?? a.wallet_balance ?? 0).toLocaleString()}`}>
            ₱{Number(wallet?.balance ?? a.wallet_balance ?? 0).toLocaleString()}
          </p>
          <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-sans mt-1">Wallet balance</p>
          <p className="text-[10px] text-[#D4AF37] font-sans mt-1 flex items-center gap-1 group-hover:text-white transition-colors" data-testid="wallet-cta">
            <ArrowUpRight className="w-3 h-3" /> Request withdrawal
          </p>
        </button>
      </div>

      {/* Top-Seller prize claim reminder — fires when there's an unclaimed
          prize with claim window still open. Self-hides when no reminder. */}
      <PrizeClaimReminder />

      {/* Motivation strip — weekly goal, streak, personal best, coach's note */}
      <MotivationStrip profile={profile} onWalletClick={() => setShowWithdraw(true)} />

      {/* Referral Links */}
      <section className="grid lg:grid-cols-2 gap-4 mb-8">
        <div data-tutorial-target="tutorial-customer-link" className="bg-[#141414] border border-[#1C7D3F]/30 p-5 sm:p-6" data-testid="amb-referral-card">
          <p className="text-xs uppercase tracking-[0.2em] text-[#1C7D3F] mb-2 font-sans font-medium">Customer Referral Link</p>
          <p className="text-white/50 text-xs font-sans mb-3">Share to earn commission on every sale (30-day cookie)</p>
          <div className="flex items-stretch gap-2">
            <div className="flex-1 bg-[#0A0A0A] border border-white/10 px-3 py-2.5 text-white/80 text-xs font-mono break-all">{profile.referral_url}</div>
            <button onClick={copyLink} data-testid="amb-copy-customer-link"
              className="flex items-center justify-center gap-2 px-4 bg-[#1C7D3F] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#2E9B50] transition-colors">
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div data-tutorial-target="tutorial-recruit-link" className="bg-[#141414] border border-[#D4AF37]/30 p-5 sm:p-6" data-testid="amb-recruit-card">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2 font-sans font-medium">Ambassador Recruit Link</p>
          <p className="text-white/50 text-xs font-sans mb-3">Share to earn <strong className="text-[#D4AF37]">₱200</strong> per new ambassador signed up + paid</p>
          <div className="flex items-stretch gap-2">
            <div className="flex-1 bg-[#0A0A0A] border border-white/10 px-3 py-2.5 text-white/80 text-xs font-mono break-all">
              {window.location.origin}/ambassador-apply?ref={a.referral_slug}
            </div>
            <button onClick={copyAmbReferralLink} data-testid="amb-copy-recruit-link"
              className="flex items-center justify-center gap-2 px-4 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#E5C158] transition-colors">
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </section>

      {/* Growth chart — last 30 days */}
      <div data-tutorial-target="tutorial-growth">
        <GrowthChart />
      </div>

      {/* Per-product analytics — which scents convert best for this ambassador */}
      <MyProductAnalytics />

      {/* Leaderboard */}
      <section data-tutorial-target="tutorial-leaderboard" className="mb-8" data-testid="dashboard-leaderboard">
        <div className="flex items-end justify-between mb-3 border-b border-white/10 pb-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans">Team Leaderboard</p>
            <h2 className="text-xl text-white font-medium mt-0.5 font-sans">Top sellers</h2>
          </div>
          <div className="flex border border-white/10 text-[10px] uppercase tracking-[0.18em] font-sans">
            {['week', 'month', 'all'].map(s => (
              <button
                key={s}
                onClick={() => reloadLeaderboard(s)}
                className={`px-3 py-1.5 transition-colors ${scope === s ? 'bg-[#D4AF37] text-black' : 'text-white/50 hover:text-white'}`}
                data-testid={`lb-scope-${s}`}
              >
                {s === 'all' ? 'All-time' : s}
              </button>
            ))}
          </div>
        </div>
        {leaderboard.length === 0 ? (
          <p className="text-white/40 text-sm font-sans py-8 text-center">No sales yet for this period. Be the first!</p>
        ) : (
          <ul className="space-y-1.5">
            {leaderboard.map((row, i) => {
              const isMe = row.ambassador_id === a.id;
              return (
                <li
                  key={row.ambassador_id}
                  className={`flex items-center gap-3 px-4 py-3 border ${isMe ? 'border-[#D4AF37]/50 bg-[#D4AF37]/5' : 'border-white/5 bg-[#0F0F0F]'} hover:border-white/15 transition-colors`}
                  data-testid={`lb-row-${i}`}
                >
                  <span className={`w-6 text-center text-xs font-sans font-medium ${i === 0 ? 'text-[#D4AF37]' : i === 1 ? 'text-[#C0C0C0]' : i === 2 ? 'text-[#CD7F32]' : 'text-white/40'}`}>
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-sans truncate">{row.name}{isMe && <span className="text-[#D4AF37] text-[10px] uppercase tracking-[0.18em] ml-2">You</span>}</p>
                    <p className="text-white/40 text-xs font-sans">{row.order_count} orders</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white text-sm font-sans">{row.bottles} btl</p>
                    <p className="text-[#D4AF37] text-xs font-sans">₱{Number(row.revenue).toLocaleString()}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Wallet transactions */}
      <section data-tutorial-target="tutorial-wallet" data-testid="dashboard-wallet">
        <div className="flex items-end justify-between mb-3 border-b border-white/10 pb-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans">Wallet Activity</p>
            <h2 className="text-xl text-white font-medium mt-0.5 font-sans">Recent transactions</h2>
          </div>
        </div>
        {!wallet || wallet.transactions.length === 0 ? (
          <div className="bg-[#0F0F0F] border border-white/10 p-6 text-center text-white/40 text-sm font-sans">
            <BadgeDollarSign className="w-6 h-6 mx-auto mb-2 text-white/20" />
            Earn ₱200 per ambassador you refer who pays for the Tester Kit. Plus commission on customer referrals.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {wallet.transactions.map(t => (
              <li key={t.id} className="flex items-center gap-3 px-4 py-3 border border-white/5 bg-[#0F0F0F]">
                <Sparkles className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-sans truncate">{t.note}</p>
                  <p className="text-white/40 text-xs font-sans">{new Date(t.created_at).toLocaleDateString('en-PH', { dateStyle: 'medium' })}</p>
                </div>
                <p className="text-[#1C7D3F] text-sm font-medium font-sans">+₱{Number(t.amount).toLocaleString()}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, subtitle, testid }) {
  return (
    <div className="bg-[#141414] border border-white/10 p-4 sm:p-5" data-testid={testid}>
      <Icon className="w-4 h-4 mb-2" style={{ color: accent }} />
      <p className="text-2xl text-white font-medium font-sans leading-tight truncate" title={String(value)}>{value}</p>
      <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-sans mt-1">{label}</p>
      {subtitle && <p className="text-[10px] text-[#D4AF37] font-sans mt-1 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" />{subtitle}</p>}
    </div>
  );
}
